import {
  ACTION_ARGS,
  Arg,
  BUTTON_ARGS,
  COMPONENT_STATE_ARGS,
  MENU_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import {
  AGENT,
  AGENT_GHOST,
  AGENT_SECONDARY,
  DESTRUCTIVE,
  GHOST,
  PRIMARY,
  SECONDARY,
  TERTIARY,
} from '../../../../utils/spds/constants/buttons';
import { BUTTON_SIZES, NORMAL } from '../../../../utils/spds/constants/sizes';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { tracked } from '@glimmer/tracking';
import AgentButton from './agent/primary.gjs';
import AgentGhostButton from './agent/ghost.gjs';
import AgentSecondaryButton from './agent/secondary.gjs';
import DestructiveButton from './destructive.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import GhostButton from './ghost.gjs';
import Menu from './split/menu.gjs';
import PrimaryButton from './primary.gjs';
import SecondaryButton from './secondary.gjs';
import Super from '../../core/menu/parent.gjs';
import TertiaryButton from './tertiary.gjs';
import Trigger from './split/trigger.gjs';
import closeOnClickOutside from '../../../../modifiers/close-on-click-outside';
import shiftFocusToParent from '../../../../modifiers/shift-focus-to-parent';
import styles from './split.module.scss';

const METADATA = Metadata.define({
  description:
    'Compound button with a main action and a dropdown with a small group of related actions. **Note:** The menu trigger is automatically hidden when no menu items are provided.',
  extends: [
    pick(ACTION_ARGS, [
      'onClose',
      'onOpen',
      {
        name: 'onClick',
        alias: 'onMainClick',
        description: 'Action triggered when the main button is clicked',
      },
    ]),
    pick(BUTTON_ARGS, [
      { name: 'buttonType', description: 'Button variant used for both halves' },
      { name: 'label', initialValue: 'Save' },
    ]),
    pick(COMPONENT_STATE_ARGS, ['autofocus', 'disabled']),
    pick(MENU_ARGS, ['menuItems', 'onItemClick']),
  ],
  meta: {
    mainDisabled: Arg.boolean({ description: 'Disable main button half' }),
    size: Arg.string({ default: NORMAL, description: 'Button size', options: BUTTON_SIZES }),
    triggerDisabled: Arg.boolean({ description: 'Disable trigger button half' }),
  },
  name: 'Split Button',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=26311-17009&m=dev',
    zeroheight:
      'https://www.simplepractice.design/1d0abbc0a/p/87c0a6-dropdown-button/t/page-329499-82233878-77c739-6',
  },
});

export default class SpdsActionButtonSplit extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-button spds-action-button';
  variantClass = 'spds-action-button-split';
  styles = styles;

  @tracked mainComponent;
  @tracked open = false;

  get button() {
    switch (this.args.buttonType) {
      case AGENT:
        return AgentButton;
      case AGENT_GHOST:
        return AgentGhostButton;
      case AGENT_SECONDARY:
        return AgentSecondaryButton;
      case DESTRUCTIVE:
        return DestructiveButton;
      case GHOST:
        return GhostButton;
      case PRIMARY:
        return PrimaryButton;
      case SECONDARY:
        return SecondaryButton;
      case TERTIARY:
        return TertiaryButton;
      default:
        return PrimaryButton;
    }
  }

  get hasMenuItems() {
    return this.items.length > 0;
  }

  get mainButton() {
    return this.mainComponent?.baseElement;
  }

  get mainId() {
    return this.composeSubcomponentId('main');
  }

  get menuId() {
    return this.composeSubcomponentId('menu');
  }

  get triggerId() {
    return this.composeSubcomponentId('trigger');
  }

  closeMenu = () => {
    this.open = false;
    this.args.onClose?.();
  };

  toggleOpenState = () => {
    this.open = !this.open;

    this.open ? this.args.onOpen?.() : this.args.onClose?.();
  };

  <template>
    <FlexRow
      @inline={{true}}
      class={{this.classes}}
      data-test-spds-action-button-split
      id={{this.id}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      {{closeOnClickOutside this.closeMenu}}
      ...attributes
    >
      <this.button
        @autofocus={{@autofocus}}
        @disabled={{or @disabled @mainDisabled}}
        @registerComponent={{fn this.registerComponent "mainComponent"}}
        @size={{@size}}
        @unregisterComponent={{fn this.unregisterComponent "mainComponent"}}
        class="spds-action-button-split-main"
        data-test-spds-action-button-split-main
        id={{this.mainId}}
        {{on "click" @onMainClick}}
        {{shiftFocusToParent}}
      >
        {{yield to="main"}}
        {{@label}}
      </this.button>

      {{#if this.hasMenuItems}}
        <Trigger
          @button={{this.button}}
          @disabled={{or @disabled @triggerDisabled}}
          @open={{this.open}}
          @size={{@size}}
          aria-controls={{this.menuId}}
          data-test-spds-action-button-split-trigger
          id={{this.triggerId}}
          {{on "click" this.toggleOpenState}}
        />
      {{/if}}

      <Menu
        @anchorElement={{this.mainButton}}
        @open={{this.open}}
        @toggleOpenState={{this.toggleOpenState}}
        aria-labelledby={{this.triggerId}}
        id={{this.menuId}}
        role="menu"
        as |menu|
      >
        {{#if (has-block "menu")}}
          {{yield
            (hash
              Item=(component
                menu.Item
                onClick=@onItemClick
                registerComponent=(fn this.registerItem false)
                unregisterComponent=this.unregisterItem
              )
            )
            to="menu"
          }}
        {{else if @menuItems}}
          {{#each @menuItems as |item|}}
            <menu.Item
              @onClick={{or item.onClick @onItemClick}}
              @registerComponent={{fn this.registerItem false}}
              @unregisterComponent={{this.unregisterItem}}
              @value={{or item.value item.label}}
            >
              {{item.label}}
            </menu.Item>
          {{/each}}
        {{/if}}
      </Menu>
    </FlexRow>
  </template>
}

export const ACTION_BUTTON_SPLIT_METADATA = SpdsActionButtonSplit.metadata;
